<template>
  <div
    v-if="tableData?.headers && tableData?.rows"
    class="size-finder__table-outter"
  >
    <div class="size-finder__table-wrapper size-finder__table-wrapper--v1">
      <table
        class="size-finder__table"
        :data-measurement-unit="getMeasurementsUnit()"
      >
        <tbody>
          <tr class="size-finder__table-header">
            <th
              v-for="(item, index) of tableData?.headers"
              :key="index"
              class="size-finder__table-header-cell text-body-xs-bold"
              v-html="headerValue(item)"
            />
          </tr>
          <tr
            v-for="(row, index) of tableData?.rows"
            :key="index"
            class="size-finder__table-row"
          >
            <td
              v-for="(cell, cellIndex) of Object.values(row)"
              :key="cellIndex"
              class="size-finder__table-cell text-body-xs-regular"
              v-html="cellValue(cell)"
            />
          </tr>
        </tbody>
      </table>
    </div>

    <div class="size-finder__table-container--v2">
      <div ref="fixedTableWrapper" class="size-finder__table-wrapper size-finder__table-wrapper--fixed">
        <table
          class="size-finder__table"
          :data-measurement-unit="getMeasurementsUnit()"
        >
          <tbody>
            <tr
              class="size-finder__table-header"
              :style="{ height: headerHeight + 'px' }"
            >
              <th
                :key="index"
                class="size-finder__table-header-cell text-body-xs-bold"
                v-html="headerValue(tableData.headers[0])"
              />
            </tr>
            <tr
              v-for="(row, index) of tableData?.rows"
              :key="index"
              class="size-finder__table-row"
              :style="{ height: rowHeights[index] + 'px' }"
            >
              <td
                :key="cellIndex"
                class="size-finder__table-cell text-body-xs-regular"
                v-html="cellValue(Object.values(row)[0])"
              />
            </tr>
          </tbody>
        </table>
      </div>

      <div
        ref="scrollTableWrapper"
        class="size-finder__table-wrapper size-finder__table-wrapper--scroll"
        js-body-scroll-lock-ignore
      >
        <table
          class="size-finder__table"
          :data-measurement-unit="getMeasurementsUnit()"
        >
          <tbody>
            <tr
              class="size-finder__table-header"
              :style="{ height: headerHeight + 'px' }"
            >
              <th
                v-for="(item, index) of tableData?.headers.slice(1)"
                :key="index"
                class="size-finder__table-header-cell text-body-xs-bold"
                v-html="headerValue(item)"
              />
            </tr>
            <tr
              v-for="(row, index) of tableData?.rows"
              :key="index"
              class="size-finder__table-row"
              :style="{ height: rowHeights[index] + 'px' }"
            >
              <td
                v-for="(cell, cellIndex) of Object.values(row).slice(1)"
                :key="cellIndex"
                class="size-finder__table-cell text-body-xs-regular"
                v-html="cellValue(cell)"
              />
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      class="size-finder__toggle-wrapper"
      :class="{ [$class('hidden')]: !enableUnitToggle }"
    >
      <span
        class="size-finder__measurement-label text-body-xxs-regular"
        :class="{ [$class('active')]: !measurementsToggleStatus }"
        v-text="$string('size_finder.centimeters')"
      />

      <label class="size-finder__measurement-switch">
        <input
          class="size-finder__measurement-input"
          :checked="measurementsToggleStatus"
          value="1"
          type="checkbox"
          @change="changeMeasurementsToggle"
        >
        <span class="size-finder__measurement-slider" />
      </label>

      <span
        class="size-finder__measurement-label text-body-xxs-regular"
        :class="{ [$class('active')]: measurementsToggleStatus }"
        v-text="$string('size_finder.inches')"
      />
    </div>
  </div>
</template>

<script>
import Papa from 'papaparse'

export default {
  name: 'SizeTable',

  props: {
    tableSource: {
      type: String,
      default: () => '',
    },
    enableUnitToggle: {
      type: [Boolean, Object],
      default: true,
    },
    switchToInches: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tableData: {
        headers: null,
        rows: null,
      },
      rowHeights: [],
      headerHeight: [],
      measurementsToggleStatus: false,
    }
  },

  watch: {

    /**
     * Watch for change in the table source.
     */
    tableSource() {
      if (this.tableSource) {
        this.createTable()
      }
    },
  },

  mounted() {
    this.createTable()
    this.setDefaultTable()

    this.observeAccordion()
    window.addEventListener("resize", this.syncRowHeights)
  },

  methods: {

    /**
     * Finds the cell heights from the 2 tables to sync heights.
     * The two tables are really one table split in two to create
     * the desired design.
     */
    async syncRowHeights() {
      await this.$nextTick()
      if (!this.$refs.fixedTableWrapper || !this.$refs.scrollTableWrapper) return

      const fixedRows = this.$refs.fixedTableWrapper.querySelectorAll(".size-finder__table-row")
      const scrollRows = this.$refs.scrollTableWrapper.querySelectorAll(".size-finder__table-row")

      this.rowHeights = [];

      fixedRows.forEach((row, index) => {
        const height1 = row.offsetHeight
        const height2 = scrollRows[index]?.offsetHeight || 0
        this.rowHeights[index] = Math.max(height1, height2)
      });

      const fixedHeader = this.$refs.fixedTableWrapper.querySelector(".size-finder__table-header")
      const scrollHeader = this.$refs.scrollTableWrapper.querySelector(".size-finder__table-header")

      if (fixedHeader && scrollHeader) {
        this.headerHeight = Math.max(fixedHeader.offsetHeight, scrollHeader.offsetHeight)
      }
    },

    /**
     * Check if accordion is active and syncs sizes.
     */
    observeAccordion() {
      const accordions = document.querySelectorAll('.accordion__trigger')

      accordions.forEach((accordion) => {
        const observer = new MutationObserver(() => {
          if (accordion.classList.contains('is-active')) {
            this.$nextTick(() => {
              this.syncRowHeights();
            });
          }
        });

        observer.observe(accordion, { attributes: true, attributeFilter: ['class'] })
      });
    },

    /**
     * Check if we want to defualt to inches.
     */
    setDefaultTable() {
      if(this.switchToInches) {
        this.changeMeasurementsToggle()
      }
    },

    /**
     * Fetches and parses the CSV data.
     */
    async createTable() {
      const sizeGuideUrl = this.tableSource

      try {
        if (sizeGuideUrl !== '') {
          const sizeGuide = await this.downloadAndParseData(sizeGuideUrl)
          this.buildSizeGuide(sizeGuide)
        }
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     * Handles the parsing of the data.
     * @param {String} url - URL where the CSV document can be downloaded.
     * @returns {Object}
     */
    downloadAndParseData(url) {
      return new Promise((resolve, reject) => {
        Papa.parse(url, {
          download: true,
          complete: (results) => {
            resolve(results)
          },
          error: (error) => {
            reject(error)
          },
        })
      })
    },

    /**
     * Adds parsed csv results to the applicable object in component data.
     * @param {Object} result - Parsed CSV data.
     */
    buildSizeGuide(results) {
      this.tableData.headers = results?.data[0]
      this.tableData.rows = results?.data.slice(1)

      this.$nextTick(() => {
        this.syncRowHeights()
      });
    },

    /**
     * Toggle table measurements value.
     */
    changeMeasurementsToggle() {
      this.measurementsToggleStatus = !this.measurementsToggleStatus
    },

    /**
     * Get current table measurements value.
     */
    getMeasurementsUnit() {
      return this.measurementsToggleStatus ? 'inch' : 'cm'
    },

    /**
     * Format table header cell value.
     * @param {String} vaue - cell value.
     * @returns {String}
     */
    headerValue(value) {
      return value.includes('(') ? `<span>${value.split('(')[0]}</span><br><span class="description">(${value.split('(')[1]}</span>` : value
    },

    /**
     * Splits cell value into cm and inches.
     * @param {String} vaue - cell value.
     * @returns {String}
     */
    cellValue(value) {
      return value.includes('|') ? `<span class="cm">${value.split('|')[0]}</span><span class="inch">${value.split('|')[1]}</span>` : value
    },
  },
}
</script>
